/**
 * ROI 计算器组件
 * 支持可配置的滑块输入和实时计算
 * 
 * @example
 * import { initROICalculator } from './components/roiCalculator.js';
 * initROICalculator({
 *   rentals: { id: 'roi-rentals', labelId: 'roi-rentals-label', min: 1, max: 50, step: 1, default: 15 },
 *   price: { id: 'roi-price', labelId: 'roi-price-label', min: 0, max: 8, step: 0.5, default: 3.5 },
 *   stations: { id: 'roi-number', labelId: 'roi-number-label', min: 1, max: 100, step: 1, default: 3 },
 *   revenueId: 'roi-revenue',
 *   daysPerMonth: 30,
 *   formatCurrency: (num) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(num)
 * });
 */
export function initROICalculator(config) {
  const {
    rentals = {},
    price = {},
    stations = {},
    revenueId,
    daysPerMonth = 30,
    formatCurrency = (num) => new Intl.NumberFormat('en-US', { 
      style: 'currency', 
      currency: 'USD', 
      maximumFractionDigits: 0 
    }).format(num),
  } = config;

  // 获取元素
  const rentalsEl = document.getElementById(rentals.id);
  const priceEl = document.getElementById(price.id);
  const stationEl = document.getElementById(stations.id);
  const rentalsLabel = document.getElementById(rentals.labelId);
  const priceLabel = document.getElementById(price.labelId);
  const stationLabel = document.getElementById(stations.labelId);
  const revenueEl = document.getElementById(revenueId);

  if (!rentalsEl || !priceEl || !stationEl || !revenueEl) {
    console.warn('ROI Calculator: Required elements not found');
    return null;
  }

  // 设置滑块属性
  const setupSlider = (input, config) => {
    if (config.min !== undefined) input.min = config.min;
    if (config.max !== undefined) input.max = config.max;
    if (config.step !== undefined) input.step = config.step;
    if (config.default !== undefined) input.value = config.default;
  };

  setupSlider(rentalsEl, rentals);
  setupSlider(priceEl, price);
  setupSlider(stationEl, stations);

  const sliderInputs = [rentalsEl, priceEl, stationEl].filter(Boolean);

  // 更新滑块填充进度
  const updateSliderFill = (input) => {
    const min = Number(input.min || 0);
    const max = Number(input.max || 100);
    const value = Number(input.value || min);
    const percent = max === min ? 0 : ((value - min) / (max - min)) * 100;
    input.style.setProperty('--roi-progress', `${percent}%`);
  };

  // 计算并更新结果
  const recalc = () => {
    const rentalsValue = rentalsEl ? Number(rentalsEl.value || 0) : 0;
    const priceValue = priceEl ? Number(priceEl.value || 0) : 0;
    const stationsValue = stationEl ? Number(stationEl.value || 0) : 0;

    // 更新标签
    if (rentalsLabel) {
      rentalsLabel.textContent = String(rentalsValue);
    }
    if (priceLabel) {
      priceLabel.textContent = `$${priceValue.toFixed(2)}`;
    }
    if (stationLabel) {
      stationLabel.textContent = String(stationsValue);
    }

    // 计算收入
    const revenue = rentalsValue * priceValue * stationsValue * daysPerMonth;
    if (revenueEl) {
      revenueEl.textContent = formatCurrency(revenue);
    }
  };

  // 处理输入事件
  const handleInput = () => {
    sliderInputs.forEach(updateSliderFill);
    recalc();
  };

  // 绑定事件
  sliderInputs.forEach((input) => {
    input.addEventListener('input', handleInput);
  });

  // 初始化
  handleInput();

  // 返回控制对象
  return {
    recalculate: recalc,
    getValues: () => ({
      rentals: Number(rentalsEl.value || 0),
      price: Number(priceEl.value || 0),
      stations: Number(stationEl.value || 0),
      revenue: Number(rentalsEl.value || 0) * Number(priceEl.value || 0) * Number(stationEl.value || 0) * daysPerMonth,
    }),
    setValues: (values) => {
      if (values.rentals !== undefined && rentalsEl) rentalsEl.value = values.rentals;
      if (values.price !== undefined && priceEl) priceEl.value = values.price;
      if (values.stations !== undefined && stationEl) stationEl.value = values.stations;
      handleInput();
    },
    destroy: () => {
      sliderInputs.forEach((input) => {
        input.removeEventListener('input', handleInput);
      });
    },
  };
}
